import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { AlertTriangle, ArrowLeft, RefreshCw } from "react-feather";
import { Link } from "react-router";
import { useGet } from "../../api";
import { ListTemplateReport } from "./list-template-report";
import {
  IWorkPipelineListOption,
  IWorkPipelineReport,
  WorkPipelineStage,
  WORK_PIPELINE_STAGE_LABELS,
  WORK_PIPELINE_STAGE_OWNER,
} from "../../interfaces";

const OWNER_VARIANT: Record<string, string> = {
  tester: "primary",
  developer: "warning",
  none: "secondary",
};

/** Whole days between now and an ISO stamp, or null when there isn't one. */
const ageInDays = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.floor(
    (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)
  );
};

/** Terminal stages aren't work — they're excluded from "outstanding". */
const isWorkStage = (stage: WorkPipelineStage) =>
  stage !== WorkPipelineStage.Complete && stage !== WorkPipelineStage.Error;

export const WorkPipelineReport: React.FC = () => {
  const [qaListId, setQaListId] = useState("");
  const [excludeAgentic, setExcludeAgentic] = useState(false);
  const [days, setDays] = useState("14");

  const { data: lists } = useGet<IWorkPipelineListOption[]>(
    "work-pipeline/lists",
    true
  );

  const url = useMemo(() => {
    const p = new URLSearchParams();
    if (qaListId) p.set("qaListId", qaListId);
    if (excludeAgentic) p.set("excludeAgentic", "true");
    p.set("days", days);
    return `work-pipeline/report?${p.toString()}`;
  }, [qaListId, excludeAgentic, days]);

  const { data, loading, refetch } = useGet<IWorkPipelineReport>(url, true);

  const outstanding = (data?.stages ?? [])
    .filter((s) => isWorkStage(s.stage))
    .reduce((sum, s) => sum + s.total, 0);
  const agedTotal = (data?.stages ?? [])
    .filter((s) => isWorkStage(s.stage))
    .reduce((sum, s) => sum + s.aged, 0);
  const busiestDay = Math.max(
    1,
    ...(data?.throughput ?? []).map((d) =>
      Math.max(d.enteredComplete, d.hunts, d.outcomesRecorded)
    )
  );
  const queues = data?.queues;
  const attention =
    (queues?.escalated ?? 0) +
    (queues?.dealMismatch ?? 0) +
    (queues?.blocksAwaitingReview ?? 0) +
    (queues?.errored ?? 0);

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Link to="/work-pipeline" className="btn btn-sm btn-outline-secondary">
            <ArrowLeft size={14} className="me-1" /> Pipeline
          </Link>
          <h4 className="mb-0">Pipeline Report</h4>
        </div>
        <div className="d-flex align-items-center gap-2">
          {data && (
            <small className="text-muted">
              As at {new Date(data.generatedAt).toLocaleTimeString()}
            </small>
          )}
          <Button variant="outline-secondary" size="sm" onClick={refetch}>
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small mb-1">QA list</Form.Label>
              <Form.Select
                size="sm"
                value={qaListId}
                onChange={(e) => setQaListId(e.target.value)}
              >
                <option value="">All lists</option>
                {(lists ?? []).map((l) => (
                  <option key={l.qaListId} value={l.qaListId}>
                    {l.qaListName} ({l.complete}/{l.total} complete)
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Activity window</Form.Label>
              <Form.Select
                size="sm"
                value={days}
                onChange={(e) => setDays(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
              </Form.Select>
            </Col>
            <Col md={4} className="pb-1">
              <Form.Check
                type="checkbox"
                id="wpr-exclude-agentic"
                className="small"
                label="Hide domains already covered by agentic testing"
                checked={excludeAgentic}
                onChange={(e) => setExcludeAgentic(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && !data ? (
        <div className="text-center p-4">
          <Spinner animation="border" size="sm" /> Loading…
        </div>
      ) : !data ? (
        <Alert variant="danger">Could not load the report.</Alert>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Card className="h-100">
                <Card.Body className="py-2">
                  <div className="text-muted small">Outstanding work</div>
                  <div className="fs-4 fw-semibold">{outstanding}</div>
                  <div className="small text-muted">
                    of {data.activeTotal} brands on the board
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body className="py-2">
                  <div className="text-muted small">Complete</div>
                  <div className="fs-4 fw-semibold text-success">
                    {data.completeTotal}
                  </div>
                  <ProgressBar
                    now={
                      data.activeTotal
                        ? (data.completeTotal / data.activeTotal) * 100
                        : 0
                    }
                    variant="success"
                    style={{ height: 6 }}
                    className="mt-2"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body className="py-2">
                  <div className="text-muted small">Claimed / unclaimed</div>
                  <div className="fs-4 fw-semibold">
                    {data.claimedTotal}
                    <span className="text-muted fs-6"> / </span>
                    <span className="text-muted">{data.unclaimedTotal}</span>
                  </div>
                  <div className="small text-muted">
                    unclaimed work is nobody&apos;s yet
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body className="py-2">
                  <div className="text-muted small">
                    Sitting over {data.agedAfterDays} days
                  </div>
                  <div
                    className={`fs-4 fw-semibold ${
                      agedTotal ? "text-warning" : ""
                    }`}
                  >
                    {agedTotal}
                  </div>
                  <div className="small text-muted">
                    in their current stage
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {attention > 0 && (
            <Alert variant="warning" className="py-2 small mb-3">
              <AlertTriangle size={14} className="me-1" />
              Needs someone:{" "}
              {[
                queues?.escalated
                  ? `${queues.escalated} escalated for help`
                  : null,
                queues?.blocksAwaitingReview
                  ? `${queues.blocksAwaitingReview} block${
                      queues.blocksAwaitingReview === 1 ? "" : "s"
                    } awaiting review`
                  : null,
                queues?.dealMismatch
                  ? `${queues.dealMismatch} deal mismatch${
                      queues.dealMismatch === 1 ? "" : "es"
                    }`
                  : null,
                queues?.errored
                  ? `${queues.errored} snapshot error${
                      queues.errored === 1 ? "" : "s"
                    }`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              . <Link to="/work-pipeline">Open the board</Link>
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Header className="py-2 small fw-bold">
              Each bucket right now
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" hover className="mb-0 small align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Bucket</th>
                    <th>Owner</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Claimed</th>
                    <th className="text-end">Unclaimed</th>
                    <th className="text-end">
                      Over {data.agedAfterDays}d
                    </th>
                    <th className="text-end">Oldest</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stages.map((s) => {
                    const oldest = ageInDays(s.oldestWaitingSince);
                    return (
                      <tr key={s.stage} className={s.total ? "" : "text-muted"}>
                        <td className="fw-semibold">
                          {WORK_PIPELINE_STAGE_LABELS[s.stage]}
                        </td>
                        <td>
                          <Badge
                            bg={
                              OWNER_VARIANT[
                                WORK_PIPELINE_STAGE_OWNER[s.stage]
                              ] ?? "secondary"
                            }
                          >
                            {WORK_PIPELINE_STAGE_OWNER[s.stage]}
                          </Badge>
                        </td>
                        <td className="text-end fw-semibold">{s.total}</td>
                        <td className="text-end">{s.claimed}</td>
                        <td className="text-end">{s.unclaimed}</td>
                        <td
                          className={`text-end ${
                            s.aged && isWorkStage(s.stage) ? "text-warning" : ""
                          }`}
                        >
                          {s.aged || "—"}
                        </td>
                        <td className="text-end text-muted">
                          {oldest === null ? "—" : `${oldest}d`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="py-2 small fw-bold">
              Movement over the last {data.throughput.length} days
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-end gap-2" style={{ height: 120 }}>
                {data.throughput.map((d) => (
                  <div
                    key={d.day}
                    className="flex-fill d-flex flex-column justify-content-end align-items-center"
                    title={`${d.day}: ${d.enteredComplete} into complete, ${d.outcomesRecorded} outcomes recorded, ${d.hunts} code hunts`}
                  >
                    <div
                      style={{
                        height: `${(d.enteredComplete / busiestDay) * 100}%`,
                        width: "100%",
                        minHeight: d.enteredComplete ? 2 : 0,
                        background: "var(--bs-success)",
                      }}
                    />
                    <div
                      style={{
                        height: `${(d.hunts / busiestDay) * 100}%`,
                        width: "100%",
                        minHeight: d.hunts ? 2 : 0,
                        background: "var(--bs-secondary)",
                      }}
                    />
                    <small
                      className="text-muted text-nowrap"
                      style={{ fontSize: 9 }}
                    >
                      {d.day.slice(5)}
                    </small>
                  </div>
                ))}
              </div>
              <div className="small text-muted mt-2">
                <Badge bg="success">&nbsp;</Badge> moved into Complete{" "}
                <Badge bg="secondary" className="ms-2">
                  &nbsp;
                </Badge>{" "}
                code hunts logged. A brand that regresses and is fixed again
                counts on both days.
              </div>
            </Card.Body>
          </Card>

          <Row className="g-3">
            <Col lg={8}>
              <Card className="h-100">
                <Card.Header className="py-2 small fw-bold">
                  By QA list
                </Card.Header>
                <Card.Body className="p-0">
                  {data.lists.length === 0 ? (
                    <div className="text-muted small p-3">
                      No lists on the board.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <Table size="sm" hover className="mb-0 small align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>List</th>
                            <th className="text-end">Done</th>
                            {data.stages
                              .filter((s) => isWorkStage(s.stage))
                              .map((s) => (
                                <th
                                  key={s.stage}
                                  className="text-end text-nowrap"
                                >
                                  {WORK_PIPELINE_STAGE_LABELS[s.stage]}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.lists.map((l) => (
                            <tr key={l.qaListId}>
                              <td className="fw-semibold">{l.qaListName}</td>
                              <td className="text-end">
                                {l.complete}/{l.total}
                              </td>
                              {data.stages
                                .filter((s) => isWorkStage(s.stage))
                                .map((s) => (
                                  <td key={s.stage} className="text-end">
                                    {l.byStage[s.stage] || (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="h-100">
                <Card.Header className="py-2 small fw-bold">
                  By country
                </Card.Header>
                <Card.Body className="p-0">
                  <Table size="sm" hover className="mb-0 small">
                    <thead className="table-light">
                      <tr>
                        <th>Country</th>
                        <th className="text-end">Complete</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.countries.map((c) => (
                        <tr key={c.countryCode}>
                          <td>{c.countryCode}</td>
                          <td className="text-end">{c.complete}</td>
                          <td className="text-end">{c.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <ListTemplateReport qaListId={qaListId} />
    </div>
  );
};
